import { autoUpdate, FloatingPortal, useFloating } from '@floating-ui/react'
import ctl from '@netlify/classnames-template-literals'
import { ReactNode } from 'react'
import { coverReferenceMiddleware } from '../../../utilities'
import { TransformerChildWrapper } from './child-wrapper'
import { TransformerGizmo } from './gizmo'
import { TransformerState } from './state'
import { TransformerStoreProvider } from './transformer-store'

export type TransformerProps = {
	children: ReactNode
	value: TransformerState
	onChange: (state: TransformerState) => void
	passthroughClassName?: string
	enableSnapping?: boolean
	enableTransform?: boolean

	enableScaling?: boolean

	onUpdateTick?: () => void
}

/**
 * Todo: support resizing (adjusting of width/hieght) instead of just scaling.
 * Scaling is still useful for images, but we should support resizing for text and floating elements.
 */
export function Transformer(
	{
		children,
		value,
		onChange,
		passthroughClassName,
		enableSnapping = true,
		enableTransform = true,
		enableScaling = true,
		onUpdateTick,
	}: TransformerProps,
) {
	return (
		<TransformerStoreProvider value={value} onChange={onChange}>
			<SubTransformer
				passthroughClassName={passthroughClassName}
				enableSnapping={enableSnapping}
				enableTransform={enableTransform}
				enableScaling={enableScaling}
				onUpdateTick={onUpdateTick}
			>
				{children}
			</SubTransformer>
		</TransformerStoreProvider>
	)
}

// We have to separate this because `autoUpdate` will rerender on scroll, which resets the store.
function SubTransformer({
	children,
	passthroughClassName,
	enableSnapping,
	enableTransform,
	enableScaling,
	onUpdateTick,
}: {
	children: ReactNode
	passthroughClassName?: string
	enableSnapping: boolean
	enableTransform: boolean
	enableScaling: boolean
	onUpdateTick?: () => void
}) {
	// we don't actually really need this... We could also try to just capture the bounding
	// box and position it via a portal. But it works for now.
	const { refs, floatingStyles } = useFloating({
		open: enableTransform,
		whileElementsMounted: autoUpdate,
		middleware: coverReferenceMiddleware(),
	})
	return (
		<>
			<div
				ref={refs.setReference}
				className={ctl(`${passthroughClassName} pointer-events-none`)}
			>
				<TransformerChildWrapper
					passthroughClassName={passthroughClassName}
				>
					{children}
				</TransformerChildWrapper>
			</div>
			{enableTransform && (
				<FloatingPortal>
					<div
						ref={refs.setFloating}
						style={floatingStyles}
						className="pointer-events-none"
						data-prevent-focus-loss
					>
						<TransformerGizmo enableSnapping={enableSnapping} enableScaling={enableScaling} />
					</div>
				</FloatingPortal>
			)}
		</>
	)
}
